/**
 * E5.4 steps 4 and 5 (ARCHITECTURE §5.17; lane F): the planet forest the sim holds. Which of the forest's cells are in the
 * sim is a pure function of the start-of-tick state (decision 154: never the camera):
 * - **a unit's band**: the cells within FOREST_HELD.bandPeople of the cell each living unit stands in (the cell, not its
 *   exact spot: the same answer for every spot in it, so it is kept by cell);
 * - **a shot's path** (step 5): a shell, a bullet or a grenade low over the ground (under FOREST_HELD.lowPeople) joins the
 *   cells its next step crosses (from its start-of-tick position and velocity, once, before anything is stepped: `flora`
 *   never changes under rounds already flown this tick). A round fired this tick makes its first step inside its
 *   shooter's band (a step is a few people; the band is 40), so it too meets only held cells — so no drawn tree takes no
 *   hit.
 * The units' bands are weighed every FOREST_HELD.bandEveryTicks (the tick count's multiples), the shots' paths every tick.
 * Cells join at the act phase's start, in ascending key (planet × 2^24 + cell), their trees appended to `state.flora` as
 * a tail — the forest function's (core/forest.ts forestCell, on the planet's ground at setup), a felled one as felled.
 * A held cell past the keep reach (the band + FOREST_HELD.keepMarginPeople) for FOREST_HELD.leaveTicks leaves: its trees
 * out of `flora`, the felled ones' ids kept (`forest.felled`), their `floraHealth` kept by id — so it comes back as it left.
 * At most FOREST_HELD.maxCells are held. Past that, the joins taken are, exactly (lane A's read): a shot's cells first; then
 * by nearness — the largest cosine between the cell's centre and the centre of any living unit's cell on its planet
 * (arithmetic only, the same on every engine; which unit is no matter: the largest); ties, and the joins taken, by key. Deterministic: integer keys, sorted
 * joins, dmath through core/cells.ts and the forest function, no shared stream.
 */
import type { FloraState, ForestHeld, GameState, PlanetState, Vec3 } from '../types';
import { cellOf, cellsNear, cellCentre, prepareCells } from './cells';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { basisPlanet, forestCell, forestCellsPerEdge, forestIdCell, forestKeepOutAt, planetForestSeed, FOREST, type ForestKeepOut } from './forest';
import { biomesFor } from './biomes';
import { reliefWoods } from './forestDensity';
import { woodsDensityOf } from './woodsDensity';
import { drainageLinesOf } from './drainageLines';
import { PHYS_DT } from '../constants/physics';

export const FOREST_HELD = {
  /** A unit holds the cells within this of the cell it stands in (people). */
  bandPeople: 40,
  /** A held cell is kept while within the band and this more of a unit's cell (people) … */
  keepMarginPeople: 40,
  /** … and leaves after this long past it (ticks: 2 s). */
  leaveTicks: Math.round(2 / PHYS_DT),
  /** A shell or a bullet under this (people over the ground) joins the cells its next step crosses. */
  lowPeople: 15,
  /** The most cells held at once (~10,000 trees at the woods' packing). */
  maxCells: 800,
  /** §0.11 step B (3), the band's cap: the most forest trees held at once — the 5,000-tree budget's half past the dressing's (sweeps/2026-10-11-trees-at-scale.md); over it, the cells nearest a unit are held first (a shot's always). Measured well under it at 64 a side on a half board (sweeps/2026-10-19-region-dressing.md). */
  maxTrees: 3000,
  /**
   * The units' bands are weighed every this many ticks (a quarter second), the shots' paths every tick: a join re-lists
   * the sim's flora tables (O(all flora): 1.2–2.3 ms at the board's few hundred, 3–7 ms at thousands), so at most four a
   * second, however the units move — a band a quarter second late is nothing at a walk.
   */
  bandEveryTicks: 30,
};

const KEY = 16777216;   // 2^24
export const forestKey = (planetIndex: number, cell: number): number => planetIndex * KEY + cell;
const keyPlanet = (key: number): number => Math.floor(key / KEY);
const keyCell = (key: number): number => key % KEY;

/**
 * 74.19's yards (core/carveYards.ts layCarve): planet `pi`'s forest placed on the carved ground — its basis moved to the
 * planet's heights now (nothing is held at setup, so no tree stood on the old basis), its relief and woods density warmed
 * again as initForest warms them (the basis: the carved generated heights where it was the planned ones, else the heights now). No forest on `pi`: the state unchanged.
 */
export function rebaseForest(state: GameState, pi: number): GameState {
  const f = state.forest, at = f?.planets.findIndex(p => p.planetIndex === pi) ?? -1;
  if (!f || at < 0) return state;
  // (the basis was the generated heights as the setup planned them — then the carved generated heights; else the heights now)
  const planet = state.planets[pi], was = f.planets[at].basis;
  const p = { ...f.planets[at], basis: was === planet.plannedHeightmap && planet.initialHeightmap ? planet.initialHeightmap : planet.heightmap };
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, windSeed = String(state.config.seed ?? ''), density = state.config.forestDensity;
  if (density) reliefWoods(basisPlanet(planet, p.basis), windSeed, person);
  if (density === 'climate') { woodsDensityOf(basisPlanet(planet, p.basis), windSeed, person); drainageLinesOf(basisPlanet(planet, p.basis), person); }   // (426 (2): the drainage lines too, as initForest)
  return { ...state, forest: { ...f, planets: f.planets.map((q, k) => (k === at ? p : q)) } };
}

/** At setup (after the dressing): the forest's planets — those the battle dressed — and its keep-out; none held yet. */
export function initForest(state: GameState): GameState {
  if (!state.config.planetForest) return state;
  const ps = personSetOf(state);
  if (ps.k >= 1) return state;
  const person = PERSON_BODY_HEIGHT * ps.k;
  const planets: ForestHeld['planets'] = [], keepOut: ForestHeld['keepOut'] = [];
  const dressed = [...new Set((state.dressedAreas ?? []).map(a => a.planetIndex))].sort((a, b) => a - b);
  for (const pi of dressed) {
    const planet = state.planets[pi];
    if (!planet || biomesFor(planet).length === 0) continue;
    planets.push({ planetIndex: pi, seed: planetForestSeed(planet), n: forestCellsPerEdge(planet, person), basis: planet.heightmap });
    for (const d of forestKeepOutAt(state, pi, person)) keepOut.push({ planetIndex: pi, dir: d.dir, angle: d.angle });
  }
  if (!planets.length) return state;
  // (the forest lattice's cell table, built here at setup: built on the first band's query, it was the match's one 16–30 ms
  // start-up hitch with the forest on — lane A's profile; a cache, so nothing the match computes changes)
  for (const p of planets) prepareCells(p.n);
  // (E5.4's density by relief: the planet's relief built here too — ~0.2 s a planet, on the first band's join it was a tick's;
  // its lee the match's wind axis, the seed the wind field reads)
  const windSeed = String(state.config.seed ?? ''), density = state.config.forestDensity;
  if (density) for (const p of planets) reliefWoods(basisPlanet(state.planets[p.planetIndex], p.basis), windSeed, person);
  // (393 (3) step 2: under 'climate' the woods density field too — the world sheet, the climate and the field, 20–60 ms a megaplanet, at setup)
  if (density === 'climate') for (const p of planets) { woodsDensityOf(basisPlanet(state.planets[p.planetIndex], p.basis), windSeed, person); drainageLinesOf(basisPlanet(state.planets[p.planetIndex], p.basis), person); }   // (426 (2): and the drainage lines the creek cut reads)
  return { ...state, forest: { planets, keepOut, held: [], out: {}, felled: [], ...(density ? { density, windSeed } : {}), ...(state.config.biomeField ? { byClimate: true as const } : {}) } };
}

/** A cell's band: the cells within `angle` of its centre (kept: a function of the cell alone). */
const bandMemo = new Map<string, readonly number[]>();
function bandOf(cell: number, n: number, angle: number): readonly number[] {
  const key = `${n}|${angle}|${cell}`;
  let b = bandMemo.get(key);
  if (!b) {
    b = cellsNear(cellCentre(cell, n), angle, n);
    if (bandMemo.size > 50000) bandMemo.clear();
    bandMemo.set(key, b);
  }
  return b;
}

const keepOutMemo = new WeakMap<ForestHeld['keepOut'], Map<number, ForestKeepOut[]>>();
export function keepOutOn(f: ForestHeld, planetIndex: number): ForestKeepOut[] {
  let m = keepOutMemo.get(f.keepOut);
  if (!m) { m = new Map(); keepOutMemo.set(f.keepOut, m); }
  let k = m.get(planetIndex);
  if (!k) { k = f.keepOut.filter(d => d.planetIndex === planetIndex).map(d => ({ dir: d.dir, angle: d.angle })); m.set(planetIndex, k); }
  return k;
}

/** A held cell's trees: the forest function's on the ground at setup, a felled one felled. */
export function heldCellTrees(f: ForestHeld, planets: readonly PlanetState[], person: number, key: number): FloraState[] {
  const pi = keyPlanet(key), cell = keyCell(key), fp = f.planets.find(p => p.planetIndex === pi);
  const planet = planets[pi];
  if (!fp || !planet) return [];
  const trees = forestCell(basisPlanet(planet, fp.basis), pi, fp.seed, person, fp.n, cell, keepOutOn(f, pi), f.density ?? 'flat', f.windSeed ?? '', f.byClimate ?? false);
  if (!f.felled.length) return trees;
  const felled = new Set(f.felled);
  return trees.map(t => (felled.has(t.id) ? { ...t, alive: false } : t));
}

/**
 * One tick's joins and leaves, from the start-of-tick state (the act phase's start: Match.stepAction). The same state
 * when nothing changes (so every table over `flora` stays as it is).
 */
export function stepForestHeld(state: GameState, tick: number, maxTrees = FOREST_HELD.maxTrees): GameState {
  const f = state.forest;
  if (!f || !f.planets.length) return state;
  const bandTick = tick % FOREST_HELD.bandEveryTicks === 0;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const want = new Set<number>(), keep = new Set<number>(), near = new Map<number, number>();   // near: a wanted key's nearest unit (angle), for the cap
  for (const fp of f.planets) {
    const planet = state.planets[fp.planetIndex];
    if (!planet || planet.alive === false) continue;
    const R = planet.radius, band = (FOREST_HELD.bandPeople * person) / R, keepA = ((FOREST_HELD.bandPeople + FOREST_HELD.keepMarginPeople) * person) / R;
    // the units' cells, once each (on a band tick)
    const unitCells = new Set<number>();
    if (bandTick) for (const u of state.players) {
      if (!u.alive || u.planetIndex !== fp.planetIndex) continue;
      const d = u.surfaceDir;
      unitCells.add(cellOf(d.x, d.y, d.z, fp.n));
    }
    for (const uc of [...unitCells].sort((a, b) => a - b)) {
      const cc = cellCentre(uc, fp.n);
      for (const c of bandOf(uc, fp.n, band)) {
        const k = forestKey(fp.planetIndex, c);
        want.add(k);
        const q = cellCentre(c, fp.n), dot = q.x * cc.x + q.y * cc.y + q.z * cc.z;
        if (!near.has(k) || dot > near.get(k)!) near.set(k, dot);
      }
      for (const c of bandOf(uc, fp.n, keepA)) keep.add(forestKey(fp.planetIndex, c));
    }
    // step 5: a shell or a bullet low over this planet joins the cells its next step crosses
    const centre = planet.position, lowAlt = FOREST_HELD.lowPeople * person;
    const path = (pos: Vec3, vel: Vec3): void => {
      const dx = pos.x - centre.x, dy = pos.y - centre.y, dz = pos.z - centre.z, r = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (r - R > lowAlt + (Math.sqrt(vel.x * vel.x + vel.y * vel.y + vel.z * vel.z) * PHYS_DT) || r < 1e-9) return;
      const ex = dx + vel.x * PHYS_DT, ey = dy + vel.y * PHYS_DT, ez = dz + vel.z * PHYS_DT, re = Math.sqrt(ex * ex + ey * ey + ez * ez) || 1;
      const a = { x: dx / r, y: dy / r, z: dz / r }, b = { x: ex / re, y: ey / re, z: ez / re };
      const mx = a.x + b.x, my = a.y + b.y, mz = a.z + b.z, ml = Math.sqrt(mx * mx + my * my + mz * mz) || 1;
      const cx = a.x - b.x, cy = a.y - b.y, cz = a.z - b.z, half = Math.sqrt(cx * cx + cy * cy + cz * cz) / 2;   // half the chord ≈ half the step's angle
      for (const c of cellsNear({ x: mx / ml, y: my / ml, z: mz / ml }, half, fp.n)) { const k = forestKey(fp.planetIndex, c); want.add(k); keep.add(k); near.set(k, 2); }   // (a shot's cells before any band's at the cap)
    };
    for (const s of state.shells ?? []) path(s.pos, s.vel);
    for (const b of state.bullets ?? []) path(b.pos, b.vel);
    for (const g of state.grenades ?? []) path(g.pos, g.vel);
  }
  // leaves (weighed on a band tick, a band's ticks at a time): a held cell out of every keep reach long enough
  let out: Record<number, number> = f.out;
  const leaving: number[] = [];
  let outChanged = false;
  if (bandTick) {
    out = {};
    for (const k of f.held) {
      if (keep.has(k)) { if (f.out[k] !== undefined) outChanged = true; continue; }
      const t = (f.out[k] ?? 0) + FOREST_HELD.bandEveryTicks;
      outChanged = true;
      if (t >= FOREST_HELD.leaveTicks) leaving.push(k); else out[k] = t;
    }
  }
  // joins: the wanted cells not held, in ascending key; past the cap, the nearest first (then the key)
  const heldSet = new Set(f.held);
  let joins = [...want].filter(k => !heldSet.has(k)).sort((a, b) => a - b);
  const room = FOREST_HELD.maxCells - (f.held.length - leaving.length);
  if (joins.length > room) joins = [...joins].sort((a, b) => (near.get(b)! - near.get(a)!) || a - b).slice(0, Math.max(0, room)).sort((a, b) => a - b);
  if (!joins.length && !leaving.length && !outChanged) return state;
  let flora = state.flora, felled = f.felled;
  if (leaving.length) {
    const gone = new Set(leaving);
    const newlyFelled: number[] = [];
    const kept = flora.filter(t => {
      if (t.id < FOREST.idBase) return true;
      const cell = forestIdCell(t.id);
      if (cell === null || !gone.has(forestKey(t.planetIndex, cell))) return true;
      if (!t.alive) newlyFelled.push(t.id);
      return false;
    });
    // (cells that leave holding no trees keep the flora the same array: a new one with the same trees refiled the walk's
    // obstacles, the hit shapes and every mover's footprints, and dropped what was memoised on them — 531 of 814 new flora
    // arrays in the Story's 900 s, sweeps/2026-11-03-slow-ticks.md)
    if (kept.length !== flora.length) flora = kept;
    if (newlyFelled.length) felled = [...new Set([...felled, ...newlyFelled])].sort((a, b) => a - b);
  }
  if (joins.length) {
    const fNow: ForestHeld = { ...f, felled };
    // the tree budget (maxTrees): the joins' trees, nearest cell first, up to what room the held trees leave; a cell past it
    // waits (it is wanted again next band tick, and joins as cells leave). Under the budget: every join, in key order, as before.
    let treesHeld = 0;
    for (const t of flora) if (t.id >= FOREST.idBase) treesHeld++;
    const byJoin = new Map<number, FloraState[]>();
    for (const k of joins) byJoin.set(k, heldCellTrees(fNow, state.planets, person, k));
    let total = treesHeld;
    for (const k of joins) total += byJoin.get(k)!.length;
    if (total > maxTrees) {
      const order = [...joins].sort((a, b) => (near.get(b) ?? 0) - (near.get(a) ?? 0) || a - b);
      const taken: number[] = [];
      for (const k of order) { const n = byJoin.get(k)!.length; if (treesHeld + n > maxTrees) continue; treesHeld += n; taken.push(k); }
      joins = taken.sort((a, b) => a - b);
    }
    const add: FloraState[] = [];
    for (const k of joins) add.push(...byJoin.get(k)!);
    if (add.length) flora = [...flora, ...add];
  }
  if (!joins.length && !leaving.length && !outChanged) return state;
  const gone = new Set(leaving);
  const held = [...f.held.filter(k => !gone.has(k)), ...joins].sort((a, b) => a - b);
  return { ...state, ...(flora !== state.flora ? { flora } : {}), forest: { ...f, held, out, felled } };
}
